import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './BookmarksGrid.styled';
import { useToastStore } from '@/store/useToastStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { BookmarksEmpty } from '@/components/BookmarksEmpty/BookmarksEmpty';
import { AdCardNew } from '@/components/AdCardNew';
import { Ad } from '@/api/types/common.types';
import { AdTransitionSource } from '@/api/types/ads.types';

interface BookmarksGridProps {
  loading: boolean;
  hasBookmarks: boolean;
  bookmarks: Ad[];
  hasBookmarkButton?: boolean;
  selection?: {
    isActive: boolean;
    selectedItems: string[];
    onToggle: (item: string) => void;
  };
  adTransitionSource?: AdTransitionSource;
}

export const BookmarksGrid = ({
  loading,
  bookmarks,
  hasBookmarks,
  hasBookmarkButton = true,
  selection,
  adTransitionSource,
}: BookmarksGridProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const { openToast } = useToastStore();

  const handleRemoveBookmark = useCallback((ad: Ad) => {
    openToast({
      image: ad?.photo?.url,
      text: t('bookmark_collections.unbookmarked'),
    });
  }, []);

  if (!loading && !hasBookmarks) {
    return <BookmarksEmpty />;
  }

  const renderLoader = () => {
    const loaderItems = isDesktop ? 8 : 6;

    return Array.from({ length: loaderItems }).map((_, index) => (
      <AdCardNew key={index} />
    ));
  };

  const getBookmarkSelection = (bookmark: Ad) => {
    if (!selection) return undefined;

    const legacyId = String(bookmark.legacyResourceId);

    return {
      isActive: selection.isActive,
      isSelected: selection.selectedItems.includes(legacyId),
      onToggle: () => selection.onToggle(legacyId),
    };
  };

  return (
    <S.BookmarksGrid data-testid="bookmarks-grid">
      {loading
        ? renderLoader()
        : bookmarks.map(bookmark => (
            <AdCardNew
              ad={bookmark}
              key={bookmark.id}
              onRemoveBookmark={handleRemoveBookmark}
              hasBookmarkButton={hasBookmarkButton}
              adTransitionSource={adTransitionSource}
              selection={getBookmarkSelection(bookmark)}
            />
          ))}
    </S.BookmarksGrid>
  );
};
